import React from 'react';
import PropTypes from 'prop-types';
import compose from 'recompose/compose';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, CircularProgress } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import { actions } from '../../actions/accountsActions';
import UserProfileForm from './UserProfileForm';
import ParticipantAccountStatusLabel from './ParticipantAccountStatusLabel';
import LoadingIndicator from '../LoadingIndicator';
import ParticipantAddressDetails from './ParticipantAddressDetails';
import { ParticipantVerificationStatus } from '../../models/auth/ParticipantVerificationStatus';
import ParticipantSettingsForm from './ParticipantSettingsForm';
import ConfirmationButton from '../ConfirmationButton';

const tabs = {
  data: 'data',
  notification: 'notification',
};

class UserProfilePage extends React.Component {
  state = {
    editMode: false,
    activeTab: tabs.data,
    deleteAccounInProgress: false,
  };  

  handleOpenEditMode = () => {
    this.setState({ editMode: true });
  }

  handleSave = data => this.props.accountsActions.updateUserProfile(data).then(() => this.setState({ editMode: false }));

  handleSaveSettings = data => this.props.accountsActions.updateSettings({ settings: data});

  handleTabChange = (event, newValue) => {
    this.setState({ activeTab: newValue });
  }

  handleDelete = () => {
    this.setState({ deleteAccounInProgress: true });
    this.props.accountsActions.deleteAccount().finally(() => this.setState({ deleteAccounInProgress: false }));
  };

  render() {
    const { fetching, participantAccount, t } = this.props;
    const { activeTab } = this.state;    
    const noOutlineStyle = {
      outline: 'none',
    };

    return (
      <div className="anonymous-panel">
        <Typography variant="h4" gutterBottom>Twoje konto</Typography>
        <Paper elevation={10}>
          <LoadingIndicator fetching={fetching}>
            {participantAccount &&
              <React.Fragment>
                 <AppBar position="static" color="default" elevation={0}>
                    <Tabs
                      value={activeTab}                      
                      indicatorColor="primary"
                      onChange={this.handleTabChange}
                    >
                      <Tab value={tabs.data} label="Dane" style={noOutlineStyle}/>
                      <Tab value={tabs.notification} label="Powiadomienia" style={noOutlineStyle}/>
                    </Tabs>
                  </AppBar>
                <div style={{ padding: 16 }}>
                  {activeTab === tabs.data && <span>
                  <div style={{textAlign: 'right'}}>
                    <ParticipantAccountStatusLabel status={participantAccount.verificationStatus} />
                  </div>
                  <Typography variant="caption" color="textSecondary">Imię</Typography>
                  <Typography gutterBottom>{participantAccount.firstName}</Typography>
                  <Typography variant="caption" color="textSecondary">Nazwisko</Typography>
                  <Typography gutterBottom>{participantAccount.lastName}</Typography>
                  <Typography variant="caption" color="textSecondary">Email</Typography>
                  <Typography gutterBottom>{participantAccount.email}</Typography>
                  <Typography variant="caption" color="textSecondary">Rodzaj konta</Typography>
                  <Typography gutterBottom>{participantAccount.participantType ? t(`auth.participantAccounts.participantTypes.${participantAccount.participantType}`) : 'Nie określono'}</Typography>

                  {participantAccount.naturalPersonData &&
                    <React.Fragment>
                      <Typography variant="caption" color="textSecondary">Numer PESEL</Typography>
                      <Typography gutterBottom>{participantAccount.naturalPersonData.pesel}</Typography>
                      <ParticipantAddressDetails address={participantAccount.naturalPersonData.address} />
                    </React.Fragment>}
                  {participantAccount.legalEntityData &&
                    <React.Fragment>
                      <Typography variant="caption" color="textSecondary">Nazwa organizacji</Typography>
                      <Typography gutterBottom>{participantAccount.legalEntityData.name}</Typography>
                      <Typography variant="caption" color="textSecondary">Numer NIP</Typography>
                      <Typography gutterBottom>{participantAccount.legalEntityData.taxId}</Typography>
                      <Typography variant="caption" color="textSecondary">Numer REGON</Typography>
                      <Typography gutterBottom>{participantAccount.legalEntityData.regon}</Typography>
                      <Typography variant="caption" color="textSecondary">Numer KRS</Typography>
                      <Typography gutterBottom>{participantAccount.legalEntityData.krs}</Typography>
                      <ParticipantAddressDetails address={participantAccount.legalEntityData.address} />
                  </React.Fragment>}
                  <div style={{ marginTop: 20 }}>
                    <Button onClick={this.handleOpenEditMode} color="primary" variant="outlined" >Zmiana danych</Button>

                    <ConfirmationButton
                        style={{ marginLeft: 20 }}
                        confirmationTitle={t('auth.userProfile.deleteAccountConfirmationTitle')}
                        confirmationText={t('auth.userProfile.deleteAccountConfirmationText')}
                        onConfirm={this.handleDelete}
                        onCancel={() => { }}
                        color="primary"
                        variant="outlined"
                      >
                        {this.state.deleteAccounInProgress && <CircularProgress size={20} />}
                        {!this.state.deleteAccounInProgress && t('auth.userProfile.deleteAccountButtonText')}
                      </ConfirmationButton>
                  </div>
                  </span>}
                  {activeTab === tabs.notification && <span>
                    <ParticipantSettingsForm
                        initialValues={participantAccount.settings}
                        onSubmit={this.handleSaveSettings}
                      />
                  </span> }
                </div>

                <Dialog open={this.state.editMode} fullWidth maxWidth="lg" onBackdropClick={() => this.setState({ editMode: false })}>
                  <DialogTitle>Zmiana danych</DialogTitle>
                  <DialogContent> 
                    {participantAccount.verificationStatus === ParticipantVerificationStatus.verified && <Typography variant="subtitle2" style={{marginBottom: 20}}>Twoje konto zostało zweryfikowane. Zmiana danych spowoduje utratę statusu i brak możliwości oddawania głosów w konsultacjach dostępnych dla zweryfikowanych uczestników.</Typography>}
                    <UserProfileForm
                      initialValues={participantAccount}
                      onSubmit={this.handleSave}                      
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => this.setState({ editMode: false })}>
                      Anuluj
                    </Button>
                  </DialogActions>
                </Dialog>
              </React.Fragment>}
          </LoadingIndicator>
        </Paper>
      </div>
    );
  }
}

UserProfilePage.propTypes = {
  t: PropTypes.func.isRequired,
  accountsActions: PropTypes.object.isRequired,
  submitting: PropTypes.bool,
  fetching: PropTypes.bool.isRequired,
  participantAccount: PropTypes.object,
};

function mapDispatchToProps(dispatch) {
  return {
    accountsActions: bindActionCreators(actions, dispatch)
  };
}

function mapStateToProps(state) {
  return {
    fetching: state.account.fetching,
    participantAccount: state.account.data,
  };
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  translate()
)(UserProfilePage);



// WEBPACK FOOTER //
// ./src/components/userProfile/UserProfilePage.js